import type {
  AmendRequestResponse,
  AnalyticsResponse,
  ApiErrorBody,
  AudiencePush,
  AuthStatusResponse,
  ConfigResponse,
  DriveSearchRequest,
  DriveSearchResponse,
  ExtractResponse,
  ExtractionResult,
  ParseBriefRequest,
  ParseBriefResponse,
  RecordAudiencePushResponse,
  RolloutDraft,
  SubmitRolloutRequest,
  SubmitRolloutResponse,
  TrackedRequestDetailResponse,
  TrackedRequestListResponse,
  ValidateBriefResponse,
  ValidationIssue,
} from '../types';

/**
 * Typed API client.
 *
 * Every failure becomes an `ApiError` carrying a code, a human-readable message
 * and any field-level issues, so screens can show inline validation without
 * parsing response bodies themselves.
 */

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly issues: ValidationIssue[];

  constructor(code: string, message: string, status: number, issues: ValidationIssue[] = []) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.issues = issues;
  }

  /** True when reconnecting Google is what the user needs to do. */
  get needsGoogle(): boolean {
    return this.code === 'NOT_AUTHENTICATED' || this.code === 'GOOGLE_AUTH_EXPIRED';
  }
}

async function request<TResponse>(
  path: string,
  init: RequestInit = {},
): Promise<TResponse> {
  let response: Response;

  try {
    response = await fetch(`/api${path}`, {
      ...init,
      // The session lives in an httpOnly cookie, so it must be sent along.
      credentials: 'same-origin',
      headers: {
        // FormData carries its own multipart Content-Type (with the boundary);
        // setting it here would corrupt the request.
        ...(init.body && !(init.body instanceof FormData)
          ? { 'Content-Type': 'application/json' }
          : {}),
        ...init.headers,
      },
    });
  } catch {
    // A rejected fetch is a transport problem, not an API response.
    throw new ApiError(
      'NETWORK_ERROR',
      'Could not reach the server. Check your connection and try again.',
      0,
    );
  }

  if (response.status === 204) return undefined as TResponse;

  const text = await response.text();
  let payload: unknown = null;
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = null;
    }
  }

  if (!response.ok) {
    const body = payload as ApiErrorBody | null;
    throw new ApiError(
      body?.error?.code ?? 'INTERNAL_ERROR',
      body?.error?.message ?? 'Something went wrong. Please try again.',
      response.status,
      body?.error?.issues ?? [],
    );
  }

  return payload as TResponse;
}

function post<TResponse>(path: string, body: unknown): Promise<TResponse> {
  return request<TResponse>(path, { method: 'POST', body: JSON.stringify(body) });
}

export interface ExtractionCapabilities {
  spreadsheet: { enabled: boolean; extensions: string[]; method: string };
  image: {
    enabled: boolean;
    mediaTypes: string[];
    /** Active engine: GOOGLE_DRIVE_OCR, VISION_MODEL, or NONE. */
    method: string;
    model: string | null;
    /** True when signing in with Google is what would enable image upload. */
    requiresGoogleSignIn: boolean;
  };
  maxUploadBytes: number;
}

export const api = {
  getAuthStatus: () => request<AuthStatusResponse>('/auth/status'),

  logout: () => post<{ ok: true }>('/auth/logout', {}),

  /** Full page navigation: the OAuth flow has to leave the SPA. */
  startGoogleConnect: () => {
    window.location.href = '/api/auth/google';
  },

  getConfig: () => request<ConfigResponse>('/config'),

  parseBrief: (input: ParseBriefRequest) => post<ParseBriefResponse>('/brief/parse', input),

  getExtractionCapabilities: () => request<ExtractionCapabilities>('/extract/capabilities'),

  /**
   * Uploads one spreadsheet or image for extraction.
   *
   * Sent as multipart rather than base64 JSON: a screenshot is often several
   * megabytes, and base64 inflates it by a third before it even leaves the
   * browser. The Content-Type header is deliberately omitted so the browser can
   * set the multipart boundary itself.
   */
  extractAttachment: async (file: File): Promise<ExtractionResult> => {
    const form = new FormData();
    form.append('file', file, file.name);

    const response = await request<ExtractResponse>('/extract', {
      method: 'POST',
      body: form,
    });
    return response.result;
  },

  validateDraft: (draft: RolloutDraft) =>
    post<ValidateBriefResponse>('/brief/validate', { draft }),

  searchDrive: (input: DriveSearchRequest) => post<DriveSearchResponse>('/drive/search', input),

  submitRollout: (input: SubmitRolloutRequest) =>
    post<SubmitRolloutResponse>('/document/submit', input),

  getAnalytics: () => request<AnalyticsResponse>('/analytics'),

  listRequests: () => request<TrackedRequestListResponse>('/requests'),

  getRequest: (id: string) =>
    request<TrackedRequestDetailResponse>(`/requests/${encodeURIComponent(id)}`),

  amendRequest: (id: string, draft: RolloutDraft) =>
    post<AmendRequestResponse>(`/requests/${encodeURIComponent(id)}/amend`, { draft }),

  recordAudiencePush: (id: string, pushes: AudiencePush[]) =>
    post<RecordAudiencePushResponse>(`/requests/${encodeURIComponent(id)}/audience-push`, {
      pushes,
    }),
};
